import React, { useCallback, useEffect, useState } from 'react';
import { getStatusSyncBoletos, StatusSyncBoletos } from '../../../api/comissionamentosApi';
import './DadosAtualizadosEm.css';

/**
 * Carimbo "Dados atualizados em" do módulo Comissionamentos (comissionamentos/at-01).
 *
 * Montado no topo das 6 telas. O que ele responde é uma pergunta só: **os boletos do SGA que
 * alimentam o repasse estão frescos?** A varredura roda no `banco-de-dados-atual` (cron das 04h
 * + botão manual) e o desfecho chega por `GET /comissionamentos/sync-boletos/status`.
 *
 * 🔴 Três estados, e a distinção entre eles é a razão do componente existir (regra da at-60):
 *
 *   | Estado                | O que a tela diz                                    |
 *   |-----------------------|-----------------------------------------------------|
 *   | Tem data              | "Dados atualizados em DD/MM/AAAA às HH:MM"          |
 *   | Nunca executou (null) | "Sincronização ainda não executada"                 |
 *   | Falha ao consultar    | "Não foi possível verificar a atualização dos dados"|
 *
 * Colapsar os dois últimos num só seria o defeito: "nunca sincronizado" é um estado legítimo do
 * sistema, e "não consegui perguntar" é uma falha. Quem lê a tela toma decisões sobre dinheiro
 * (repasse de comissionamento) — carimbar uma data que não se conseguiu confirmar, ou dizer que
 * nunca houve sync quando o backend só está fora, leva à conclusão errada nos dois sentidos.
 *
 * ⚠️ Erro aqui é FAIXA, não toast: o toast some em segundos e o carimbo é informação
 * permanente da tela. E a falha não esconde o resto da tela — ela convive com o conteúdo.
 */

/** Formata sem inventar fuso: o backend manda ISO em UTC e o navegador exibe no fuso local. */
const formatar = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

interface Props {
  /** Sobe a cada disparo bem-sucedido, para o carimbo reconsultar sem recarregar a página. */
  recarregarEm?: number;
}

const DadosAtualizadosEm: React.FC<Props> = ({ recarregarEm = 0 }) => {
  const [status, setStatus] = useState<StatusSyncBoletos | null>(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setStatus(await getStatusSyncBoletos());
      setErro(false);
    } catch {
      // O estado seguro aqui é `erro`, NUNCA `status = null`: null é "nunca sincronizado", que
      // é uma afirmação sobre o sistema. Falha de consulta não autoriza essa afirmação.
      setStatus(null);
      setErro(true);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar, recarregarEm]);

  if (carregando && !status && !erro) {
    return <p className="comis-carimbo comis-carimbo-neutro">Verificando atualização dos dados...</p>;
  }

  if (erro) {
    return (
      <p className="comis-carimbo comis-carimbo-erro" role="alert">
        Não foi possível verificar a atualização dos dados. Recarregue a página; se persistir,
        acione a TI.
      </p>
    );
  }

  const tentativa = status?.ultimaTentativa ?? null;
  // A última tentativa falhou DEPOIS da última atualização boa? É o caso que a tela precisa
  // contar: os dados continuam válidos, mas pararam de ser atualizados a partir dali.
  const tentativaFalhou = Boolean(tentativa && !tentativa.trouxeDados);

  if (!status?.ultimaAtualizacao) {
    return (
      <p className="comis-carimbo comis-carimbo-neutro">
        {tentativaFalhou ? (
          <>
            Nenhuma sincronização de boletos concluiu com dados ainda — a última tentativa, em{' '}
            <strong>{formatar(tentativa!.em)}</strong>, não conseguiu trazer boletos do SGA.
          </>
        ) : (
          'Sincronização de boletos ainda não executada.'
        )}
        {status?.emAndamento ? ' Uma sincronização está em andamento agora.' : ''}
      </p>
    );
  }

  return (
    <p className="comis-carimbo">
      Dados atualizados em <strong>{formatar(status.ultimaAtualizacao)}</strong>
      {/* ⚠️ A tentativa mais recente falhou e não trouxe nada: o dado acima segue válido, mas é
          mais antigo do que o usuário esperaria — dizer só a data esconderia isso. */}
      {tentativaFalhou && (
        <span className="comis-carimbo-aviso">
          {' '}
          — a tentativa de <strong>{formatar(tentativa!.em)}</strong> não conseguiu trazer boletos
          do SGA, então os dados não avançaram desde então.
        </span>
      )}
      {!tentativaFalhou && status.ultimoStatus === 'FALHA_PARCIAL' && (
        // Trouxe dado, mas não tudo: um número da tela pode estar desatualizado sem nada mais
        // indicar isso.
        <span className="comis-carimbo-aviso">
          {' '}
          — a última sincronização terminou com falhas parciais, e parte dos boletos pode não ter
          sido atualizada.
        </span>
      )}
      {status.emAndamento && <span className="comis-carimbo-aviso"> — sincronização em andamento.</span>}
    </p>
  );
};

export default DadosAtualizadosEm;
